import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { CheckCircle, BookOpen, FileText, Target, ArrowRight, ClipboardCheck, HelpCircle } from 'lucide-react';

const faqs = [
  {
    question: "What is the role of the Curriculum & Standards Authority?",
    answer: "The Curriculum & Standards Authority develops, maintains, and updates competency frameworks that underpin all IMCAC assessments and accreditation activities. This includes defining competency outcomes, establishing case log requirements, designing OSCE blueprints, and creating standardized assessment rubrics."
  },
  {
    question: "How are competency frameworks developed?",
    answer: "Competency frameworks are developed through a rigorous process involving subject matter experts, academic advisors, and practicing clinicians. They are aligned with international medical education standards and undergo regular review to incorporate the latest evidence-based practices."
  },
  {
    question: "What are the minimum case log requirements?",
    answer: "Case log requirements vary by certification level and specialty. Generally, candidates must document supervised procedures across multiple categories, including patient assessment, treatment delivery, complication management, and follow-up care. Specific numbers are outlined in each examination's requirements."
  },
  {
    question: "How often are curriculum standards updated?",
    answer: "All competency frameworks and assessment standards undergo annual review by the Curriculum Committee. Updates incorporate feedback from examiners, practitioners, academic advisors, and external benchmarking against international standards."
  },
  {
    question: "Can training providers submit curricula for IMCAC alignment?",
    answer: "Yes, training providers can submit their curricula for review and alignment with IMCAC standards. Approved curricula receive recognition, and their graduates may be eligible for streamlined examination pathways. Submit applications through the Curriculum Application portal."
  },
  {
    question: "What competency domains are assessed across IMCAC certifications?",
    answer: "Core competency domains include: Facial Anatomy & Physiology, Patient Assessment & Selection, Treatment Planning & Execution, Complication Prevention & Management, Pharmacology, Ethics & Documentation, Emergency Protocols, Infection Control, Evidence-Based Practice, and Professional Development."
  }
];

export default function CurriculumPage() {
  return (
    <div data-testid="curriculum-page">
      <section className="bg-[#1B2A4A] py-16 md:py-20">
        <div className="imcac-container">
          <p className="imcac-overline mb-3">Division B</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">Curriculum & Standards Authority</h1>
          <div className="h-1 w-20 bg-[#C5A572] mt-5" />
        </div>
      </section>

      <section className="imcac-section bg-white">
        <div className="imcac-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <h2 className="text-3xl font-semibold text-[#1B2A4A] font-heading mb-6">Standards Development</h2>
              <p className="text-base text-slate-600 leading-relaxed mb-6">
                The Curriculum & Standards Authority Division is responsible for developing, maintaining, and 
                updating the competency frameworks that underpin all IMCAC assessments and accreditation activities.
              </p>

              <h3 className="text-xl font-semibold text-[#1B2A4A] font-heading mb-4">Core Responsibilities</h3>
              <div className="space-y-4 mb-8">
                {[
                  { icon: Target, title: 'Competency Framework Development', desc: 'Define measurable competency outcomes for each certification level, aligned with international medical education standards.' },
                  { icon: FileText, title: 'Case Log Requirements', desc: 'Establish minimum case volumes, procedure categories, and documentation standards for logbook submissions.' },
                  { icon: BookOpen, title: 'OSCE Blueprint Publication', desc: 'Design and publish station blueprints ensuring comprehensive coverage of clinical competencies.' },
                  { icon: ClipboardCheck, title: 'Assessment Rubric Creation', desc: 'Develop standardised marking criteria for written, clinical, and oral assessments.' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4 p-4 bg-[#F8FAFC] rounded-sm border border-slate-100">
                    <item.icon className="h-5 w-5 text-[#C5A572] flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-semibold text-[#1B2A4A]">{item.title}</h4>
                      <p className="text-sm text-slate-600 mt-1">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <h3 className="text-xl font-semibold text-[#1B2A4A] font-heading mb-4">Competency Domains</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
                {[
                  'Facial Anatomy & Physiology',
                  'Patient Assessment & Selection',
                  'Treatment Planning & Execution',
                  'Complication Prevention & Management',
                  'Pharmacology & Product Knowledge',
                  'Ethics, Consent & Documentation',
                  'Emergency Protocols',
                  'Infection Control & Safety',
                  'Evidence-Based Practice',
                  'Professional Development',
                ].map((domain, i) => (
                  <div key={i} className="flex items-center gap-2 p-2.5 bg-white border border-slate-200 rounded-sm">
                    <CheckCircle className="h-4 w-4 text-[#C5A572] flex-shrink-0" />
                    <span className="text-sm text-slate-700">{domain}</span>
                  </div>
                ))}
              </div>

              <Link to="/apply/curriculum" data-testid="curriculum-pathways-link">
                <Button className="bg-[#1B2A4A] hover:bg-[#152039] text-white rounded-sm">
                  Submit Curriculum Application <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>

            <div className="lg:col-span-5">
              <img
                src="https://images.unsplash.com/photo-1758574437877-68a1ec5fafc3?auto=format&fit=crop&w=800&q=80"
                alt="Medical review"
                className="w-full h-64 object-cover rounded-sm shadow-md mb-6"
              />
              <Card className="imcac-card">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-[#1B2A4A] font-heading mb-3">Standards Review Cycle</h3>
                  <p className="text-sm text-slate-600 leading-relaxed mb-4">
                    All competency frameworks and assessment standards undergo annual review by the Curriculum Committee, 
                    incorporating feedback from examiners, practitioners, and academic advisors.
                  </p>
                  <ul className="space-y-2">
                    {['Annual content review', 'Stakeholder consultation', 'Evidence-based updates', 'External benchmarking'].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                        <CheckCircle className="h-4 w-4 text-[#C5A572]" /> {item}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="imcac-section bg-slate-50">
        <div className="imcac-container max-w-4xl">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#1B2A4A]/5 rounded-sm mb-3">
              <HelpCircle className="h-3 w-3 text-[#C5A572]" />
              <span className="text-xs font-semibold tracking-wider text-[#1B2A4A] uppercase">FAQs</span>
            </div>
            <h2 className="text-2xl font-bold text-[#1B2A4A] font-heading">Frequently Asked Questions</h2>
          </div>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border-b border-slate-200">
                <AccordionTrigger className="text-left font-semibold text-[#1B2A4A] hover:text-[#C5A572] hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}
